import type { TFunction } from 'i18next';
import { PASSWORD_LENGTH } from '@cribbage/protocol';

/**
 * The checks every account form runs before it asks the server, and the hint
 * under every new-password field.
 *
 * Signing in, registering, the Account rows and the reset pages all ask for an
 * email or a new password. Each kept its own copy of these lines, so the same
 * mistake could read differently on two screens. They mirror the server's
 * constraints so the common mistakes cost nothing; the server stays the
 * authority and its answer is shown as-is.
 */

/** Why an email address will not do, or `undefined` when it might. */
export function emailProblem(value: string, t: TFunction): string | undefined {
  const address = value.trim();
  if (address === '') return t('auth.emailRequired');
  if (!looksLikeEmail(address)) return t('auth.emailInvalid');
  return undefined;
}

/** Why a new password is too short, or `undefined` when it is long enough. */
export function newPasswordProblem(value: string, t: TFunction): string | undefined {
  if (value.length >= PASSWORD_LENGTH.min) return undefined;
  return t('auth.passwordTooShort', { count: PASSWORD_LENGTH.min });
}

/** The hint under a new-password field: the length rule, before it is broken. */
export function newPasswordHint(t: TFunction): string {
  return t('auth.passwordHint', { count: PASSWORD_LENGTH.min });
}

/** The same loose shape the server's own check accepts. Not a validator. */
function looksLikeEmail(address: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
}
